/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, Heading, Text, VFlow } from 'bold-ui'
import { gray } from 'bold-ui/lib/styles/colors'
import { useFlags } from 'config/useFlagsContext'
import { ReactComponent as Consulta } from 'images/historico/icone-consulta.svg'
import { ReactComponent as WeightIcon } from 'images/medical/weight.svg'
import { ReactComponent as AnexoArquivosIcon } from 'images/novidades/anexo-arquivos.svg'
import { ReactComponent as AcompanhamentoCondicoesDeSaudeIcon } from 'images/side-menu/acompanhamentos.svg'
import { ReactComponent as AtividadeColetivaIcon } from 'images/side-menu/atividade-coletiva.svg'
import { ReactComponent as GarantiaAcessoIcon } from 'images/side-menu/garantia-acesso.svg'
import { ReactComponent as Relatorios } from 'images/side-menu/relatorios.svg'
import PuericulturaIcon from 'images/soap/ic_puericultura.png'

import { Carousel, CarouselItem } from './carousel/Corousel'
import { NewsBalancedTopicsSection } from './NewsBalancedTopicsSection'
import { NewsGif } from './NewsGif'
import { NewsHeadingSection } from './NewsHeadingSection'
import { NewsImg } from './NewsImg'

export function NewsInternalPanelCincoQuatro() {
  const {
    PILOTO_INTEGRACAO_REGULACAO_ENABLED,
    PILOTO_COMPARTILHAMENTO_EMULTI_ENABLED,
    CUIDADO_COMPARTILHADO_CIDADAO_OBITO_ENABLED,
    GARANTIA_ACESSO_CIDADAO_OBITO,
  } = useFlags()

  if (!process.env.REACT_APP_VERSION) {
    return (
      <Alert type='danger' inline>
        Nenhuma informação sobre versões anteriores está disponível.
      </Alert>
    )
  }

  return (
    <VFlow vSpacing={1}>
      <Text fontSize={0.875} fontWeight='bold'>
        Novidades:
      </Text>
      <NewsBalancedTopicsSection>
        <Text fontSize={0.875} component='li'>
          Anexo de arquivos no prontuário
        </Text>
        <Text fontSize={0.875} component='li'>
          Atualização da Ficha de notificação de caso suspeito
        </Text>
        <Text fontSize={0.875} component='li'>
          Avaliação do M-CHAT-R na puericultura
        </Text>
        <Text fontSize={0.875} component='li'>
          Novas funcionalidades para o Acompanhamento de condições de saúde
        </Text>
        <Text fontSize={0.875} component='li'>
          Novas funcionalidades para o Cuidado compartilhado
        </Text>
        {GARANTIA_ACESSO_CIDADAO_OBITO && (
          <Text fontSize={0.875} component='li'>
            Indicação de óbito na Garantia de acesso
          </Text>
        )}
        <Text fontSize={0.875} component='li'>
          Novo relatório para monitoramento de envios para RNDS
        </Text>
        <Text fontSize={0.875} component='li'>
          Novo relatório gerencial de exames
        </Text>
        <Text fontSize={0.875} component='li'>
          Novos gráficos para crianças, adolescentes e gestantes
        </Text>
        <Text fontSize={0.875} component='li'>
          Novo módulo e melhorias no registro de Atividades coletivas
        </Text>
        {PILOTO_INTEGRACAO_REGULACAO_ENABLED && (
          <Text fontSize={0.875} component='li'>
            Notificações no módulo de Regulação assistencial
          </Text>
        )}
        {PILOTO_INTEGRACAO_REGULACAO_ENABLED && (
          <Text fontSize={0.875} component='li'>
            Regulação assistencial
          </Text>
        )}
      </NewsBalancedTopicsSection>
      <NewsHeadingSection
        title='Anexo de arquivos no prontuário'
        icon={<AnexoArquivosIcon css={styles.iconSVG} />}
        id='anexo-arquivos-5-4'
      >
        <VFlow vSpacing={0.5}>
          <Text fontSize={0.875} component='li'>
            Agora será possível <strong>anexar alguns tipos de arquivo</strong> no prontuário de um cidadão durante um
            atendimento. O arquivo pode ser anexado no “<strong>Objetivo</strong>” do SOAP e é possível incluir algumas
            informações de identificação do arquivo.
          </Text>
          <NewsGif
            src='/static/novidades/5-4_anexar_arquivo.webp'
            pausedSrc='/static/novidades/5-4_anexar_arquivo_paused.png'
            alt='Gif mostrando o anexo de um arquivo'
          />
          <Text fontSize={0.875} component='li'>
            Os arquivos anexados podem ser visualizados no <strong>histórico</strong> de atendimentos e na{' '}
            <strong>listagem de arquivos</strong> no “Objetivo” do SOAP, dentro do prontuário do cidadão. Na listagem, é
            possível incluir filtros e ordenar pela data de adição.
          </Text>
          <NewsGif
            src='/static/novidades/5-4_buscar_arquivo.webp'
            pausedSrc='/static/novidades/5-4_buscar_arquivo_paused.png'
            alt='Gif mostrando a listagem de arquivos, com a inclusão de um filtro e a alteração de ordenação'
          />
          <Text fontSize={0.875} component='li'>
            Para habilitar a funcionalidade, o administrador de instalação deve preparar a{' '}
            <strong>infraestrutura</strong> e configurar a funcionalidade nas{' '}
            <strong>configurações da instalação</strong>.
          </Text>
        </VFlow>
      </NewsHeadingSection>

      <NewsHeadingSection
        title='Atualização da Ficha de notificação de caso suspeito'
        icon={<Consulta css={styles.iconSVG} />}
        id='notificacao-ficha-sinan-5-4'
      >
        <VFlow vSpacing={0.5}>
          <Text fontSize={0.875}>
            A ficha de notificação utilizada na finalização dos atendimentos foi atualizada para refletir o modelo mais
            recente, mantendo a padronização para todos os tipos de doenças e agravos.
          </Text>
          <Text fontSize={0.875}>As principais mudanças foram:</Text>
          <Text fontSize={0.875} component='li'>
            Inclusão de novas doenças e agravos;
          </Text>
          <Text fontSize={0.875} component='li'>
            Remoção de doenças e agravos obsoletos;
          </Text>
          <Text fontSize={0.875} component='li'>
            Correção e padronização dos nomes.
          </Text>
          <Text fontSize={0.875}>
            Também foi atualizada a estrutura da ficha impressa, com novos campos e melhorias na disposição das
            informações, permitindo um preenchimento mais completo e organizado.
          </Text>
          <NewsImg
            src='/static/novidades/5-4_notificacao_ficha_sinan.png'
            alt='Imagem exibindo as notificações da Ficha SINAN'
          />
        </VFlow>
      </NewsHeadingSection>

      <NewsHeadingSection
        title='Avaliação do M-CHAT-R na puericultura'
        icon={<img src={PuericulturaIcon} alt='Puericultura' css={css(styles.iconSVG, styles.fillableIconSVG)} />}
        id='mchat-5-4'
      >
        <VFlow vSpacing={0.5}>
          <Text fontSize={0.875} component='li'>
            Agora no atendimento de <strong>Puericultura de crianças de 16 a 30 meses</strong> é possível realizar a
            avaliação do <strong>M-CHAT-R (Modified Checklist for Autism in Toddlers, Revised)</strong>, um instrumento
            de triagem amplamente utilizado para identificar sinais precoces de transtorno do espectro autista (TEA) em
            crianças. Com essa atualização, profissionais da saúde podem aplicar, registrar e analisar os resultados do
            M-CHAT-R diretamente no sistema.
          </Text>
          <NewsGif
            src='/static/novidades/5-4_m_chat_r_puericultura.webp'
            pausedSrc='/static/novidades/5-4_m_chat_r_puericultura_paused.png'
            alt='Gif mostrando como acessar o M-CHAT-R através da puericultura e o preenchimento do instrumento.'
          />
          <Text fontSize={0.875} component='li'>
            O último resultado avaliado ficará disponível na puericultura, podendo ser impresso em formato PDF.
          </Text>
          <NewsGif
            src='/static/novidades/5-4_m_chat_r_posterior_puericultura.webp'
            pausedSrc='/static/novidades/5-4_m_chat_r_posterior_puericultura_paused.png'
            alt='Gif mostrando um resultado do M-CHAT-R em um atendimento posterior ao preenchimento e como imprimir esse resultado a partir da puericultura.'
          />
          <Text fontSize={0.875} component='li'>
            Ainda os demais resultados avaliados durante o atendimento de puericultura, ficarão disponíveis na modal de
            Acompanhamento da criança, podendo ser impressos em formato PDF a qualquer momento.
          </Text>
          <NewsGif
            src='/static/novidades/5-4_m_chat_r_acompanhamento_da_crianca.webp'
            pausedSrc='/static/novidades/5-4_m_chat_r_acompanhamento_da_crianca_paused.png'
            alt='Gif mostrando a modal de acompanhamento da criança com vários resultados do M-CHAT-R na listagem e como imprimir um desses resultados.'
          />
        </VFlow>
      </NewsHeadingSection>

      <NewsHeadingSection
        title='Novas funcionalidades para o Acompanhamento de condições de saúde'
        icon={<AcompanhamentoCondicoesDeSaudeIcon css={css(styles.iconSVG, styles.fillableIconSVG)} />}
        id='acompanhamento-condicoes-saude-5-4'
      >
        <VFlow vSpacing={0.5}>
          <Alert type='info' inline>
            Iniciamos uma mudança gradual do termo{' '}
            <Text color='info' fontWeight='bold'>
              Cidadão
            </Text>{' '}
            para o termo{' '}
            <Text color='info' fontWeight='bold'>
              Pessoa
            </Text>{' '}
            no sistema, o módulo de{' '}
            <Text color='info' fontWeight='bold'>
              Acompanhamento de condições de saúde
            </Text>{' '}
            agora utiliza a nova terminologia.
          </Alert>
          <Heading level={4}>Listagens temáticas</Heading>
          <Text fontSize={0.875}>
            O módulo de Acompanhamento de condições de saúde foi aprimorado com Listagens temáticas, permitindo a busca
            por diversas condições específicas, como:
          </Text>
          <Text fontSize={0.875} component='li'>
            Gestação e puerpério;
          </Text>
          <Text fontSize={0.875} component='li'>
            Pessoa idosa;
          </Text>
          <Text fontSize={0.875} component='li'>
            Desenvolvimento infantil;
          </Text>
          <Text fontSize={0.875} component='li'>
            Diabetes;
          </Text>
          <Text fontSize={0.875} component='li'>
            Hipertensão;
          </Text>
          <Text fontSize={0.875} component='li'>
            Saúde da mulher;
          </Text>
          <Text fontSize={0.875} component='li'>
            Saúde bucal.
          </Text>
          <Text fontSize={0.875}>
            A listagem{' '}
            <Text fontSize={0.875} fontWeight='bold'>
              Geral{' '}
            </Text>
            será mantida como padrão inicial, permitindo uma visão ampla das pessoas acompanhadas antes da aplicação de
            filtros temáticos específicos.
          </Text>
          <Text fontSize={0.875}>
            Todas as listagens apresentarão dados e indicadores de boas práticas na atenção primária à saúde, oferecendo
            maior suporte ao cuidado e à tomada de decisão pelas equipes. Os dados e indicadores são provenientes do
            Prontuário Eletrônico e-SUS APS. A tabela agora permite expansão para visualizar informações detalhadas,
            sendo que cada lista possui suas próprias particularidades.
          </Text>
          <Heading level={4}>Geral</Heading>
          <Text fontSize={0.875} component='li'>
            Implementação de novos campos e reordenação de informações da listagem.
          </Text>
          <NewsGif
            src='/static/novidades/5-4_lista_tematica_geral.gif'
            pausedSrc='/static/novidades/5-4_lista_tematica_geral_paused.png'
            alt='Gif mostrando como utilizar a lista temática geral.'
          />
          <Heading level={4}>Gestação e puerpério</Heading>
          <Text fontSize={0.875} component='li'>
            Implementação da listagem temática de{' '}
            <Text fontSize={0.875} fontWeight='bold'>
              Gestação e puerpério
            </Text>
            , que permite rastrear as gestantes que possuem gestação ativa no Prontuário Eletrônico, bem como as
            gestantes que entraram em puerpério.
          </Text>
          <NewsGif
            src='/static/novidades/5-4_lista_tematica_gestacao_puerperio.gif'
            pausedSrc='/static/novidades/5-4_lista_tematica_gestacao_puerperio_paused.png'
            alt='Gif mostrando como utilizar a lista temática de gestação e puerpério.'
          />
          <Heading level={4}>Pessoa idosa</Heading>
          <Text fontSize={0.875} component='li'>
            Implementação da listagem temática de{' '}
            <Text fontSize={0.875} fontWeight='bold'>
              Pessoa idosa
            </Text>
            , que permite a visualização de boas práticas e o rastreamento das condições de saúde das pessoas com 60
            anos ou mais.
          </Text>
          <Text fontSize={0.875} component='li'>
            Agora é possível filtrar por pessoas idosas (60 anos ou mais) que tiveram ou não o preenchimento do IVCF-20
            (Índice de Vulnerabilidade Clínico-funcional) no Acompanhamento de condições de saúde. É possível filtrar
            pelo índice, dimensões alteradas e pontuação. É possível também buscar por pessoas idosas que não tiveram o
            formulário preenchido.
          </Text>
          <Text fontSize={0.875} component='li'>
            O filtro está disponível somente na listagem temática da{' '}
            <Text fontSize={0.875} fontWeight='bold'>
              Pessoa idosa
            </Text>{' '}
            na opção de filtros avançados.
          </Text>
          <NewsGif
            src='/static/novidades/5-4_lista_tematica_pessoa_idosa.gif'
            pausedSrc='/static/novidades/5-4_lista_tematica_pessoa_idosa_paused.png'
            alt='Gif mostrando como utilizar a lista temática de pessoa idosa.'
          />
          <Heading level={4}>Desenvolvimento infantil</Heading>
          <Text fontSize={0.875} component='li'>
            Implementação da listagem temática de{' '}
            <Text fontSize={0.875} fontWeight='bold'>
              Desenvolvimento infantil
            </Text>
            , que permite rastrear as crianças até 2 anos completos.
          </Text>
          <NewsGif
            src='/static/novidades/5-4_lista_tematica_dev_infantil.gif'
            pausedSrc='/static/novidades/5-4_lista_tematica_dev_infantil_paused.png'
            alt='Gif mostrando como utilizar a lista temática de desenvolvimento infantil.'
          />
          <Heading level={4}>Diabetes</Heading>
          <Text fontSize={0.875} component='li'>
            Implementação da listagem temática de{' '}
            <Text fontSize={0.875} fontWeight='bold'>
              Diabetes
            </Text>
            , que permite a visualização de boas práticas e o rastreamento das pessoas que possuem as condições
            relacionadas.
          </Text>
          <NewsGif
            src='/static/novidades/5-4_lista_tematica_diabetes.gif'
            pausedSrc='/static/novidades/5-4_lista_tematica_diabetes_paused.png'
            alt='Gif mostrando como utilizar a lista temática de diabetes.'
          />
          <Heading level={4}>Hipertensão</Heading>
          <Text fontSize={0.875} component='li'>
            Implementação da listagem temática de{' '}
            <Text fontSize={0.875} fontWeight='bold'>
              Hipertensão
            </Text>
            , que permite a visualização de boas práticas e o rastreamento das pessoas que possuem as condições
            relacionadas à hipertensão arterial.
          </Text>
          <NewsGif
            src='/static/novidades/5-4_lista_tematica_hipertensao.gif'
            pausedSrc='/static/novidades/5-4_lista_tematica_hipertensao_paused.png'
            alt='Gif mostrando como utilizar a lista temática de hipertensão.'
          />
          <Heading level={4}>Saúde da mulher</Heading>
          <Text fontSize={0.875}>
            Implementação da listagem temática de{' '}
            <Text fontSize={0.875} fontWeight='bold'>
              Saúde da mulher
            </Text>
            , permite visualizar as boas práticas para pessoas do sexo feminino entre 14 e 69 anos, classificadas em
            grupos de rastreamento conforme os seguintes critérios:
            <ul>
              <Text fontSize={0.875} component='li'>
                Rastreamento de Infecções Sexualmente Transmissíveis (IST), que visa olhar para a atenção da saúde
                sexual e reprodutiva: Mulheres entre 14 e 69 anos que receberam atendimento relacionado.
              </Text>
              <Text fontSize={0.875} component='li'>
                Rastreamento de câncer do colo do útero: Mulheres entre 25 e 64 anos que realizaram ao menos um exame de
                rastreamento.
              </Text>
              <Text fontSize={0.875} component='li'>
                Rastreamento de câncer de mama: Mulheres entre 50 e 69 anos que realizaram ao menos um exame de
                mamografia.
              </Text>
            </ul>
            Essa listagem permitirá um acompanhamento mais eficiente e direcionado, contribuindo para a prevenção e o
            cuidado contínuo dessas mulheres.
          </Text>
          <NewsGif
            src='/static/novidades/5-4_lista_saude_mulher.webp'
            pausedSrc='/static/novidades/5-4_lista_saude_mulher_paused.png'
            alt='Gif mostrando a seleção da lista temática de saúde da mulher, a pesquisa e o resultado'
          />
          <Heading level={4}>Saúde bucal</Heading>
          <Text fontSize={0.875} component='li'>
            Implementação da listagem temática de <strong>Saúde bucal</strong>, que permite a visualização de boas
            práticas e o rastreamento de cidadãos que possuam condições relacionadas.
          </Text>
          <NewsGif
            src='/static/novidades/5-4_lista_saude_bucal.webp'
            pausedSrc='/static/novidades/5-4_lista_saude_bucal_paused.png'
            alt='Gif mostrando a seleção da lista temática de saúde bucal, a pesquisa e o resultado'
          />
        </VFlow>
      </NewsHeadingSection>

      <NewsHeadingSection
        title='Novas funcionalidades para o Cuidado compartilhado'
        icon={<GarantiaAcessoIcon css={styles.iconSVG} />}
        id='novas-funcionalidades-cuidado-5-4'
      >
        <VFlow vSpacing={0.5}>
          <Text fontSize={0.875}>
            O módulo de Cuidado Compartilhado foi atualizado com recursos que tornam o acompanhamento mais eficiente e
            alinhado à rotina das equipes. Entre as novidades, destacam-se melhorias na contagem de tempo de resposta e
            a introdução de notificações visuais para sinalizar novas evoluções nos cuidados.
          </Text>
          <Text fontSize={0.875}>
            A contagem de tempo dos cuidados compartilhados foi atualizada para ser pausada no período entre sexta-feira
            às 17:59 até segunda-feira às 00:01.
          </Text>
          <Text fontSize={0.875}>
            Além disso, para facilitar o acompanhamento dos profissionais em cuidados compartilhados nos quais atuam
            como solicitantes ou executantes, o módulo passou a contar com notificações visuais em tempo real no menu
            lateral e na lista de cuidados. Agora, sempre que houver novos cuidados ou evoluções ainda não visualizadas,
            será exibido um contador no menu lateral, ao lado do item “Gestão de filas”. Dentro do módulo, os registros
            com novidades não visualizadas também serão destacados na listagem, facilitando a identificação rápida.
          </Text>
          <NewsImg
            src='/static/novidades/5-4_notificacao_cuidado_menu_lateral.png'
            alt='Imagem exibindo as notificações de Cuidado compartilhado no menu lateral'
          />
          <Text fontSize={0.875}>O que gera notificação:</Text>
          <Text fontSize={0.875} component='li'>
            Criação de um novo cuidado com executante definido;
          </Text>
          <Text fontSize={0.875} component='li'>
            Criação de um cuidado sem executante, gerando notificação para todos os profissionais da unidade com o CBO
            compatível;
          </Text>
          <Text fontSize={0.875} component='li'>
            Evolução de um cuidado com alteração no status (por exemplo, de “Respondido” para “Aguardando”).
          </Text>
          <Text fontSize={0.875}>
            O que encerra a notificação:
            <ul>
              <Text fontSize={0.875} component='li'>
                No menu lateral:
              </Text>
              <ul>
                <Text fontSize={0.875} component='li'>
                  Ao acessar o módulo com notificações pendentes.
                </Text>
              </ul>
              <Text fontSize={0.875} component='li'>
                Dentro do módulo:
              </Text>
              <ul>
                <Text fontSize={0.875} component='li'>
                  Quando o profissional acessa o cuidado (visualiza, discute o caso ou torna-se executante).
                </Text>
                <Text fontSize={0.875} component='li'>
                  Quando outro profissional se torna responsável pelo cuidado.
                </Text>
              </ul>
            </ul>
          </Text>
          <Text fontSize={0.875}>Veja como será exibido:</Text>
          <NewsImg
            src='/static/novidades/5-4_notificacao_cuidado_lista.png'
            alt='Imagem exibindo as notificações de Cuidado compartilhado na listagem'
          />
          {(PILOTO_COMPARTILHAMENTO_EMULTI_ENABLED || CUIDADO_COMPARTILHADO_CIDADAO_OBITO_ENABLED) && (
            <ul css={styles.noMarginUl}>
              <VFlow vSpacing={0.5}>
                {PILOTO_COMPARTILHAMENTO_EMULTI_ENABLED && (
                  <VFlow vSpacing={0.5}>
                    <Text fontSize={0.875} component='li'>
                      Agora o módulo de Cuidado compartilhado permite aos profissionais eMulti compartilhar cuidados
                      com:
                    </Text>
                    <ul>
                      <Text fontSize={0.875} component='li'>
                        Profissionais eMulti de sua equipe;
                      </Text>
                      <Text fontSize={0.875} component='li'>
                        Profissionais das equipes vinculadas.
                      </Text>
                    </ul>
                  </VFlow>
                )}
                {CUIDADO_COMPARTILHADO_CIDADAO_OBITO_ENABLED && (
                  <VFlow vSpacing={0.5}>
                    <Text fontSize={0.875} component='li'>
                      Criação de tratamento para cuidados compartilhados de cidadãos falecidos, com as seguintes
                      atualizações:
                    </Text>
                    <ul>
                      <Text fontSize={0.875} component='li'>
                        Ao acessar o cuidado pelo botão "Visualizar discussão", será exibida uma mensagem informando que
                        o cidadão faleceu.
                      </Text>
                      <Text fontSize={0.875} component='li'>
                        Cuidados com status "Respondido" serão alterados automaticamente para o novo status "Finalizado
                        por óbito".
                      </Text>
                      <Text fontSize={0.875} component='li'>
                        O botão "Discutir" será desativado em cuidados com o status "Finalizado por óbito".
                      </Text>
                      <Text fontSize={0.875} component='li'>
                        Cuidados de cidadãos falecidos com o status "Aguardando" permitirão apenas a conduta de
                        "Recondução de cuidado".
                      </Text>
                    </ul>
                  </VFlow>
                )}
              </VFlow>
            </ul>
          )}
        </VFlow>
      </NewsHeadingSection>

      <NewsHeadingSection
        title='Novo relatório para monitoramento de envios para a RNDS'
        id='relatorio-envio-sucesso-rnds-5-4'
        icon={<Relatorios css={css(styles.iconSVG, styles.fillableIconSVG)} />}
      >
        <VFlow vSpacing={0.5}>
          <Text fontSize={0.875}>
            Agora é possível gerar relatórios de envios da RNDS que foram realizados com sucesso. O propósito da
            funcionalidade é possibilitar que os Administradores municipais tenham acesso ao envios que foram feitos com
            sucesso para a RNDS.
          </Text>
          <Text fontSize={0.875}>
            Em conjunto ao lançamento do relatório de sucesso realizou-se adaptações nas telas de envio da RNDS que
            possui o caminho "Gestão municipal {'>'} aba 'RNDS'" e poderá ser acessado por usuários com o perfil de
            "Administrador municipal". As opções de geração do relatório são:
          </Text>
          <Text fontSize={0.875} component='li'>
            "Status da tentativa de envio" com as opções: "Com erros" e "Com sucesso";
          </Text>
          <Text fontSize={0.875} component='li'>
            "Período do relatório";
          </Text>
          <Text fontSize={0.875} component='li'>
            "Tipo de registro": RIAC, RIAR, RAC e RPM;
          </Text>
          <Text fontSize={0.875} component='li'>
            Botões "Cancelar" e "Gerar relatório".
          </Text>
          <NewsImg src='/static/novidades/5-4_tela_rnds.png' alt='Imagem da tela da RNDS' />
          <Text fontSize={0.875}>
            Ao clicar no botao "Gerar relatório" a tabela "Relatórios em processamento/processados (7 dias)" exibirá uma
            nova linha com o relatório, em caso de sucesso será possível exportar a planilha com as informações
            solicitadas pelo usuário.
          </Text>
          <NewsImg
            src='/static/novidades/5-4_tabela_relatorios_processados_rnds.png'
            alt='Imagem da tela da RNDS e da tabela de relatórios processados'
          />
          <Text fontSize={0.875}>
            O relatório deve conter um resumo das informações inseridas pelo usuário nos campos "Status da tentativa de
            envio", "Período do relatório" e "Tipo de registro".
          </Text>
          <NewsImg
            src='/static/novidades/5-4_relatorio_csv_rnds.png'
            alt='Imagem do relatório CSV de envios com sucesso da RNDS'
          />
        </VFlow>
      </NewsHeadingSection>

      <NewsHeadingSection
        title='Novo relatório gerencial de exames'
        id='relatorio-gerencial-exames-5-4'
        icon={<Relatorios css={css(styles.iconSVG, styles.fillableIconSVG)} />}
      >
        <VFlow vSpacing={0.5}>
          <Text fontSize={0.875}>
            Estamos disponibilizando um novo relatório gerencial que permite uma análise detalhada dos{' '}
            <Text fontSize={0.875} fontWeight='bold'>
              exames solicitados e avaliados
            </Text>{' '}
            durante os atendimentos realizados. Este recurso foi desenvolvido para oferecer maior visibilidade e
            facilitar o acompanhamento de informações relacionadas aos exames.
          </Text>
          <ul>
            <VFlow vSpacing={0.5}>
              <Text fontSize={0.875} component='li'>
                <Text fontSize={0.875} fontWeight='bold'>
                  Cobertura ampliada
                </Text>
                : Inclui os dados de exames registrados em atendimentos individuais e odontológicos do Prontuário
                Eletrônico, além das informações registradas no CDS.
              </Text>
              <Text fontSize={0.875} component='li'>
                <Text fontSize={0.875} fontWeight='bold'>
                  Compatibilidade com procedimentos odontológicos
                </Text>
                : Para os atendimentos odontológicos, considera procedimentos do grupo SIGTAP 02 - Procedimentos com
                finalidade diagnóstica, registrados no CDS e no odontograma do Prontuário Eletrônico.
              </Text>
              <NewsGif
                src='/static/novidades/5-4_relatorio_gerencial_exames_geracao.gif'
                pausedSrc='/static/novidades/5-4_relatorio_gerencial_exames_geracao_paused.png'
                alt='Gif mostrando como gerar um relatório'
              />
              <Text component='li'>
                <Text fontSize={0.875} fontWeight='bold'>
                  Análise por origem
                </Text>
                : Visualize o volume de exames solicitados e avaliados por meio do campo “Origem”.
              </Text>
              <NewsGif
                src='/static/novidades/5-4_relatorio_gerencial_exames_campo_origem.gif'
                pausedSrc='/static/novidades/5-4_relatorio_gerencial_exames_campo_origem_paused.png'
                alt='Gif mostrando como utilização do campo de origem'
              />
              <Text component='li'>
                <Text fontSize={0.875} fontWeight='bold'>
                  Exportação de dados
                </Text>
                : Permite a exportação dos resultados em arquivos CSV, garantindo maior flexibilidade no uso e
                compartilhamento das informações.
              </Text>
              <NewsImg
                src='/static/novidades/5-4_relatorio_gerencial_exames_csv.png'
                alt='Imagem exibindo a exportação do relatório para csv'
              />
            </VFlow>
          </ul>
        </VFlow>
      </NewsHeadingSection>

      <NewsHeadingSection
        title='Novos gráficos para crianças, adolescentes e gestantes'
        icon={<WeightIcon css={css(styles.iconSVG, styles.fillableIconSVG)} />}
        id='graficos-criancas-adolescentes-gestantes-5-4'
      >
        <VFlow vSpacing={0.5}>
          <Text fontSize={0.875} component='li'>
            Com foco no aprimoramento do acompanhamento nutricional, o sistema recebeu atualizações importantes nos
            gráficos de peso e altura para crianças e adolescentes, além da inclusão de gráficos específicos para o
            ganho de peso gestacional. As alterações seguem as diretrizes do Guia de Vigilância Alimentar e Nutricional
            na Atenção Primária à Saúde (APS).
          </Text>
          <Text fontSize={0.875} component='li'>
            O gráfico de altura foi atualizado para adolescentes de 10 a 19 anos. A classificação para o gráfico de
            altura conta com as opções "adequado", "baixo" e "muito baixo". Sendo que as opções "muito elevado" e
            "elevado" não serão mais exibidas.
          </Text>
          <NewsImg
            src='/static/novidades/5-4_grafico_altura_adolescentes.png'
            alt='Imagem exibindo gráfico de altura para adolescentes'
          />
          <Text fontSize={0.875} component='li'>
            Além disso, o sistema conta com os gráficos de peso e altura atualizados para crianças de 0 a 10 anos. A
            classificação para o gráfico de peso conta com as opções "elevado", "adequado", 'baixo" e "muito baixo".
            Sendo que a opção "muito elevado" não será mais exibida.
          </Text>
          <NewsImg
            src='/static/novidades/5-4_grafico_peso_criancas.png'
            alt='Imagem exibindo gráfico de peso para crianças'
          />
          <Text fontSize={0.875} component='li'>
            A classificação para o gráfico de altura conta com as opções "adequado", "baixo" e "muito baixo". Sendo que
            as opções "muito elevado" e "elevado" não serão mais exibidas.
          </Text>
          <NewsImg
            src='/static/novidades/5-4_grafico_altura_criancas.png'
            alt='Imagem exibindo gráfico de altura para crianças'
          />
          <Text fontSize={0.875} component='li'>
            Também o sistema agora conta com gráficos de ganho de peso específicos para acompanhar a evolução de acordo
            com o Índice de Massa Corporal (IMC) de referência da pessoa gestante. Essa funcionalidade estará disponível
            dentro da aba "Gestação", no card de "Medições", para gestantes que iniciaram o pré-natal no sistema.
          </Text>
          <NewsGif
            src='/static/novidades/5-4_acesso_grafico_ganho_peso.gif'
            pausedSrc='/static/novidades/5-4_acesso_grafico_ganho_peso_paused.png'
            alt='Gif mostrando como acessar o gráfico de ganho de peso gestacional'
          />
          <Text fontSize={0.875} component='li'>
            Com essa atualização, o sistema exibirá um dos gráficos a seguir, conforme o IMC de referência da gestante:
          </Text>
          <ul>
            <VFlow vSpacing={0.5}>
              <Text fontSize={0.875} component='li'>
                <Text fontSize={0.875} fontWeight='bold'>
                  Baixo Peso:
                </Text>{' '}
                IMC menor que 18,5 kg/m²
              </Text>
              <NewsImg
                src='/static/novidades/5-4_grafico_ganho_peso_baixopeso.png'
                alt='Imagem exibindo gráfico de ganho de peso para gestantes com baixo peso'
              />
              <Text fontSize={0.875} component='li'>
                <Text fontSize={0.875} fontWeight='bold'>
                  Eutrofia:
                </Text>{' '}
                IMC maior ou igual a 18,5 kg/m² e menor que 25 kg/m²
              </Text>
              <NewsImg
                src='/static/novidades/5-4_grafico_ganho_peso_eutrofia.png'
                alt='Imagem exibindo gráfico de ganho de peso para gestantes com eutrofia'
              />
              <Text fontSize={0.875} component='li'>
                <Text fontSize={0.875} fontWeight='bold'>
                  Sobrepeso:
                </Text>{' '}
                IMC maior ou igual a 25 kg/m² e menor que 30 kg/m²
              </Text>
              <NewsImg
                src='/static/novidades/5-4_grafico_ganho_peso_sobrepeso.png'
                alt='Imagem exibindo gráfico de ganho de peso para gestantes com sobrepeso'
              />
              <Text fontSize={0.875} component='li'>
                <Text fontSize={0.875} fontWeight='bold'>
                  Obesidade:
                </Text>{' '}
                IMC maior ou igual a 30 kg/m²
              </Text>
              <NewsImg
                src='/static/novidades/5-4_grafico_ganho_peso_obesidade.png'
                alt='Imagem exibindo gráfico de ganho de peso para gestantes com obesidade'
              />
            </VFlow>
          </ul>
          <Text fontSize={0.875} component='li'>
            Para o cálculo do IMC de referência, o sistema considera a medição de peso e altura mais recente realizada
            até 2 meses antes do início da gestação. Caso não exista essa informação, considera-se a primeira medição de
            peso e altura realizada até a oitava semana após o início da gestação.
          </Text>
          <Text fontSize={0.875} component='li'>
            As atualizações foram adaptadas do "Guia para a Organização da Vigilância Alimentar e Nutricional na Atenção
            Primária à Saúde, 2023".
          </Text>
        </VFlow>
      </NewsHeadingSection>

      <NewsHeadingSection
        title='Novo módulo e melhorias no registro de Atividades coletivas'
        icon={<AtividadeColetivaIcon css={css(styles.iconSVG, styles.fillableIconSVG)} />}
        id='atividade-coletiva-5-4'
      >
        <VFlow vSpacing={0.5}>
          <Text fontSize={0.875}>
            O registro de atividades coletivas passou por uma reestruturação completa, com a chegada de um novo módulo.
            Além de facilitar o preenchimento e a gestão dos registros, essa atualização trouxe novos recursos como
            temas aprimorados, filtros, observações por cidadão e visualização de registros de diferentes fontes.
          </Text>
          <Text fontSize={0.875}>
            Com o objetivo de otimizar o preenchimento existente na ficha e trazer evoluções ao processo de registro de
            atividades coletivas nas unidades, o novo módulo conta com:
          </Text>
          <ul>
            <VFlow vSpacing={0.5}>
              <Text fontSize={0.875} component='li'>
                Separação dos registros em Reuniões e Atividade em saúde;
              </Text>
              <Text fontSize={0.875} component='li'>
                Visualização de registros de atividade coletiva de toda localidade do profissional;
              </Text>
              <Text fontSize={0.875} component='li'>
                Gestão da listagem de registros através dos filtros de profissional responsável, registros processados,
                período, tipo de atividade e equipe;
              </Text>
              <Text fontSize={0.875} component='li'>
                Campo aberto para inclusão de anotações da atividade em saúde ou reunião;
              </Text>
              <Text fontSize={0.875} component='li'>
                Preenchimento automático de dados do cidadão e do profissional;
              </Text>
              <Text fontSize={0.875} component='li'>
                Cálculo e classificação automática de IMC;
              </Text>
              <Text fontSize={0.875} component='li'>
                Inclusão de observações sobre cidadãos em registros de reunião.
              </Text>
            </VFlow>
          </ul>
          <Text fontSize={0.875}>Confira a nova visualização e gestão da listagem dos registros:</Text>
          <NewsGif
            src='/static/novidades/5-4_listagem_atividade_coletiva.gif'
            pausedSrc='/static/novidades/5-4_listagem_atividade_coletiva_paused.png'
            alt='Gif mostrando a visualização e gestão da listagem de registros de atividade coletiva no novo módulo do PEC'
          />
          <Text fontSize={0.875}>
            Agora, o registro das atividades está otimizado através do preenchimento automático de dados, além de contar
            com novo campo para detalhamento da atividade:
          </Text>
          <NewsGif
            src='/static/novidades/5-4_registro_atividade_coletiva.webp'
            pausedSrc='/static/novidades/5-4_registro_atividade_coletiva_paused.png'
            alt='Gif mostrando o registro de atividade coletiva no novo módulo do PEC'
          />
          <Text fontSize={0.875}>
            Além disso, os registros de reunião contam com uma nova seção para inclusão de observações sobre cidadãos
            que foram discutidas durante a reunião de equipe:
          </Text>
          <NewsGif
            src='/static/novidades/5-4_observacoes_atividade_coletiva.webp'
            pausedSrc='/static/novidades/5-4_observacoes_atividade_coletiva_paused.png'
            alt='Gif mostrando o registro de obsevações sobre cidadãos em registros de reunião de equipe'
          />

          <Text fontSize={0.875}>
            O novo módulo de Atividade coletiva também conta com atualizações no campo "Temas para saúde", que passa a
            ter novas regras específicas de habilitação e novas opções de conteúdo.
          </Text>

          <Text fontSize={0.875}>Principais alterações:</Text>
          <ul>
            <Text fontSize={0.875} component='li'>
              Inclusão do campo <strong>Temas para saúde</strong> nos registros de reunião, habilitado apenas quando
              "Educação permanente" for o tema exclusivo da reunião.
            </Text>
            <Text fontSize={0.875} component='li'>
              Novas opções de <strong>Temas para saúde</strong> adicionadas, tanto para os registros de reunião quanto
              para os de atividade em saúde: Amamentação e Alimentação complementar saudável.
            </Text>
          </ul>
          <NewsGif
            src='/static/novidades/5-4_atualizacoes_temas_em_saude_atividade_coletiva.webp'
            pausedSrc='/static/novidades/5-4_atualizacoes_temas_em_saude_atividade_coletiva_paused.png'
            alt='Gif mostrando as atualizações em temas para saúde no módulo de Atividade coletiva'
          />
          <Text fontSize={0.875}>
            A listagem do módulo de Atividade coletiva passa a exibir também os registros anteriores ao seu lançamento,
            bem como os registros provenientes de outras fontes, como Registro de Atendimento Simplificado (RAS PEC),
            aplicativo e-SUS AC, CDS e sistemas de terceiros. Além disso, foram adicionados os filtros "Fonte dos
            registros" e "Origem", permitindo uma análise mais detalhada e uma gestão visual mais eficiente dos dados
            com base em suas respectivas origens.
          </Text>
          <Carousel>
            <CarouselItem
              image='/static/novidades/5-4_exibicao_registros_atividade_coletiva.png'
              alt='Imagem exibindo registros no módulo de Atividade coletiva'
            />
            <CarouselItem
              image='/static/novidades/5-4_exibicao_registros_outras_fontes_atividade_coletiva.png'
              alt='Imagem exibindo registros de outras fontes no módulo de Atividade coletiva'
            />
          </Carousel>
          <Text fontSize={0.875}>
            A listagem do Histórico do Cidadão passa a exibir novos registros e dados originados do módulo de Atividade
            coletiva. Dentre as principais atualizações, temos:
          </Text>
          <ul>
            <Text fontSize={0.875} component='li'>
              Exibição do campo de Anotações da atividade ao expandir o registro de Atividade coletiva, tanto para
              atividades em saúde quanto para atividades em reunião;
            </Text>
            <Text fontSize={0.875} component='li'>
              Nova exibição de registros de reunião de equipe no Histórico do Cidadão para quando o mesmo for mencionado
              em alguma Observação sobre cidadão durante a atividade.
            </Text>
          </ul>
          <Carousel>
            <CarouselItem
              image='/static/novidades/5-4_ac_anotacoes_atividade_ao_expandir_acordeon.png'
              alt='Anotações de Atividade ao Expandir Registro de Atividade coletiva'
            />
            <CarouselItem
              image='/static/novidades/5-4_ac_exibicao_registros_reuniao_historico_cidadao.png'
              alt='Exibição de registros de reunião no histórico do cidadão'
            />
          </Carousel>
        </VFlow>
      </NewsHeadingSection>

      {PILOTO_INTEGRACAO_REGULACAO_ENABLED && (
        <NewsHeadingSection
          title='Notificações no módulo de Regulação assistencial'
          icon={<GarantiaAcessoIcon css={styles.iconSVG} />}
          id='notificacao-regulacao-5-4'
        >
          <VFlow vSpacing={0.5}>
            <Text fontSize={0.875}>
              O módulo de Regulação assistencial passou a contar com notificações visuais no menu lateral e na listagem
              de solicitações, mantendo os profissionais informados sobre alterações importantes nos encaminhamentos,
              exames e OCIs.
            </Text>
            <Text fontSize={0.875}>
              Sempre que uma solicitação for atualizada para os status “Devolvido pela regulação”, “Atendimento
              agendado” ou “Negada pela regulação”, o profissional solicitante será notificado com um contador no menu
              lateral e um ícone de leitura destacado na linha correspondente da listagem.
            </Text>
            <Text fontSize={0.875}>Atualização de uma solicitação para os status:</Text>
            <Text fontSize={0.875} component='li'>
              Devolvido pela regulação;
            </Text>
            <Text fontSize={0.875} component='li'>
              Atendimento agendado;
            </Text>
            <Text fontSize={0.875} component='li'>
              Negada pela regulação.
            </Text>
            <Text fontSize={0.875}>
              O que encerra a notificação:
              <ul>
                <Text fontSize={0.875} component='li'>
                  No menu lateral:
                </Text>
                <ul>
                  <Text fontSize={0.875} component='li'>
                    Ao acessar o módulo com notificações pendentes.
                  </Text>
                </ul>
                <Text fontSize={0.875} component='li'>
                  Dentro do módulo:
                </Text>
                <ul>
                  <Text fontSize={0.875} component='li'>
                    Quando o profissional solicitante clica para visualizar a solicitação.
                  </Text>
                  <Text fontSize={0.875} component='li'>
                    Quando o status da solicitação é alterado para qualquer outro diferente de “Devolvido pela
                    regulação”, “Atendimento agendado” ou “Negada pela regulação”.
                  </Text>
                </ul>
              </ul>
            </Text>
            <Text fontSize={0.875}>Veja como será exibido:</Text>
            <NewsImg
              src='/static/novidades/5-4_notificacao_regulacao_menu_lateral.png'
              alt='Imagem exibindo as notificações de Regulação assistencial no menu lateral'
            />
            <NewsImg
              src='/static/novidades/5-4_notificacao_regulacao_lista.png'
              alt='Imagem exibindo as notificações de Regulação assistencial na listagem'
            />
          </VFlow>
        </NewsHeadingSection>
      )}

      {PILOTO_INTEGRACAO_REGULACAO_ENABLED && (
        <NewsHeadingSection
          title='Regulação assistencial'
          icon={<GarantiaAcessoIcon css={styles.iconSVG} />}
          id='regulacao-assistencial-5-4'
        >
          <VFlow vSpacing={0.5}>
            <Text fontSize={0.875} component='li'>
              Agora no módulo da <strong>Garantia de Acesso</strong>, é apresentada a possibilidade de consultar a aba
              de <strong>Regulação Assistencial</strong>. Nessa seção, os profissionais poderão acompanhar as
              solicitações de Exames e Guias de Encaminhamento geradas nos atendimentos que passam pelo processo de
              regulação assistencial. O PEC irá receber atualizações de status para essas solicitações através de uma
              integração com os sistemas de regulação próprios configurados pelo administrador da instalação.
            </Text>
            <Text fontSize={0.875} component='li'>
              As solicitações poderão ser filtradas por{' '}
              <strong>Unidade e Equipe Responsável, Tipo de Solicitação e Status</strong>. Além de ser possível filtrar
              pelo profissional solicitante e o período de realização da solicitação.
            </Text>
            <NewsGif
              src='/static/novidades/5-4_selecao_e_pesquisa_RA.webp'
              pausedSrc='/static/novidades/5-4_selecao_e_pesquisa_RA_paused.png'
              alt='Gif mostrando a abertura da aba de Garantia de acesso e seleção da regulação assistencial'
            />
            <Text fontSize={0.875} component='li'>
              Todos os profissionais com acesso aos atendimentos que podem solicitar Exames e Guia de Encaminhamento,
              poderão ter acesso à aba da <strong>Regulação Assistencial</strong>, dentro do módulo de{' '}
              <strong>Garantia de Acesso</strong>.
            </Text>
            <NewsGif
              src='/static/novidades/5-4_filtro_RA.webp'
              pausedSrc='/static/novidades/5-4_filtro_RA_paused.png'
              alt='Gif mostrando a aba da regulação assistencial e a utilização do filtro secundário de "Solicitante"'
            />
            <Text fontSize={0.875} component='li'>
              Ao visualizar um registro de solicitação, é possível ver o seu <strong>histórico de atualizações</strong>{' '}
              para entender como está o processo de regulação assistencial daquele registro no sistema de regulação.
            </Text>
            <NewsGif
              src='/static/novidades/5-4_modal_RA.webp'
              pausedSrc='/static/novidades/5-4_modal_RA_paused.png'
              alt='Gif mostrando a modal de visualização da regulação, com o histórico de atualizações do registro sendo expandido'
            />
          </VFlow>
        </NewsHeadingSection>
      )}

      {GARANTIA_ACESSO_CIDADAO_OBITO && (
        <NewsHeadingSection title='Indicação de óbito na Garantia de acesso' id='indicacao-obito-ga-5-4'>
          <VFlow vSpacing={0.5}>
            <Text fontSize={0.875}>
              Agora, os registros da Garantia de Acesso contam com uma nova indicação visual para sinalizar quando o
              cidadão está falecido. Essa melhoria visa evitar tentativas de agendamento de consultas para cidadãos
              falecidos, reduzindo erros e aumentando a clareza para os profissionais de saúde.
            </Text>
            <ul>
              <Text fontSize={0.875} component='li'>
                Principais atualizações:
              </Text>
              <ul>
                <Text fontSize={0.875} component='li'>
                  Indicação visual de óbito nos registros da Garantia de Acesso.
                </Text>
                <Text fontSize={0.875} component='li'>
                  Bloqueio da funcionalidade de agendamento para cidadãos falecidos, prevenindo tentativas indevidas.
                </Text>
                <Text fontSize={0.875} component='li'>
                  Novo motivo de remoção do registro: "Óbito do cidadão".
                </Text>
              </ul>
            </ul>
            <Text fontSize={0.875}>
              Essas mudanças garantem que os registros representem a situação do cidadão, melhorando a gestão na
              Garantia de Acesso.
            </Text>
          </VFlow>
        </NewsHeadingSection>
      )}
    </VFlow>
  )
}

const styles = {
  iconSVG: css`
    margin-right: 0.25rem;
    width: 2rem;
    height: 2rem;
  `,
  fillableIconSVG: css`
    fill: ${gray.c60};
  `,
  noMarginUl: css`
    margin: 0;
  `,
}
